<div class="login-container">
  @if (isLoading) {
    <div class="spinner">
      <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
    </div>
  } @else {
    <div class="auth-card">
      <div class="top-row">
        @if (!platform.isNative) {
          <button type="button" class="back-btn" (click)="goBack()" [attr.aria-label]="'shell.nav.back' | translate">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        } @else {
          <span></span>
        }
        <app-language-switcher></app-language-switcher>
      </div>
      @if (isClinicPage) {
        <div class="clinic-brand">
          @if (clinicLogo) {
            <img [src]="clinicLogo" class="clinic-logo" [alt]="clinicName" />
          } @else {
            <div class="clinic-name">{{ clinicName }}</div>
          }
          <div class="powered-by">
            <span>{{ 'auth.login.poweredBy' | translate }}</span>
            <img [src]="brand.logoSrc" class="brand-logo-small" [alt]="'a11y.logoAlt' | translate" />
          </div>
        </div>
        <h2>{{ 'auth.login.clinicTitle' | translate: { clinic: clinicName } }}</h2>
        @if (inviteInvalid) {
          <div class="error">{{ 'auth.login.inviteInvalid' | translate }}</div>
        }
        <label class="consent-row">
          <input type="checkbox" name="consent" [checked]="consent" (change)="onConsentChange($any($event.target).checked)" [disabled]="busy" />
          <span>
            @if (inviteDoctorName) {
              {{ 'auth.login.clinicConsentDoctor' | translate: { doctor: inviteDoctorName, clinic: clinicName } }}
            } @else {
              {{ 'auth.login.clinicConsent' | translate: { clinic: clinicName } }}
            }
          </span>
        </label>
      } @else {
        <img [src]="brand.logoSrc" class="brand-logo" [alt]="'a11y.logoAlt' | translate" />
        @if (clinicMissing) {
          <div class="error">{{ 'auth.login.clinicNotFound' | translate }}</div>
        }
        <h2>{{ 'auth.login.title' | translate }}</h2>
      }

      @if (mode === 'password') {
        <form (submit)="$event.preventDefault(); submitPassword()">
          <input
            type="email"
            name="email"
            [placeholder]="'auth.login.email' | translate"
            autocomplete="email"
            [(ngModel)]="email"
            [disabled]="busy"
          />
          <input
            type="password"
            name="password"
            [placeholder]="'auth.login.password' | translate"
            autocomplete="current-password"
            [(ngModel)]="password"
            [disabled]="busy"
          />
          @if (errorMsg) {
            <div class="error">{{ errorMsg }}</div>
          }
          <button type="submit" class="primary" [disabled]="busy">
            {{ (busy ? 'auth.login.signingIn' : 'auth.login.signInBtn') | translate }}
          </button>
          <button type="button" class="link" (click)="forgotPassword()" [disabled]="busy">
            {{ 'auth.login.forgotPassword' | translate }}
          </button>
        </form>
      } @else {
        <form (submit)="$event.preventDefault(); submitMagic()">
          <input
            type="email"
            name="email"
            [placeholder]="'auth.login.email' | translate"
            autocomplete="email"
            [(ngModel)]="email"
            [disabled]="busy || magicSent"
          />
          @if (errorMsg) {
            <div class="error">{{ errorMsg }}</div>
          }
          @if (magicSent) {
            <div class="success">{{ 'auth.login.magicSent' | translate }}</div>
          } @else {
            <button type="submit" class="primary" [disabled]="busy">
              {{ (busy ? 'auth.login.sending' : 'auth.login.sendMagicLink') | translate }}
            </button>
          }
        </form>
      }

      <button type="button" class="link" (click)="toggleMode()" [disabled]="busy">
        {{ (mode === 'password' ? 'auth.login.useMagicLink' : 'auth.login.usePassword') | translate }}
      </button>

      <!-- Correction brief 1.8: after eleven minutes of work, say plainly that
           the work is safe and what the account is for. Only when there really
           is a finished check-in waiting. -->
      @if (hasPendingCheckin) {
        <p class="checkin-safe">{{ 'auth.pendingCheckin' | translate }}</p>
      }

      <div class="divider"><span>{{ 'auth.login.or' | translate }}</span></div>

      <div class="social-block" [class.needs-consent]="needsConsent">

      <!-- Sign in with Apple — shown on iOS (App Store 4.8: required when other
           third-party logins are offered) and on web (so a Hide-My-Email user
           continues on the same account on desktop). Native Android omits it
           (would need the web-redirect flow). -->
      @if (platform.isIOS || !platform.isNative) {
        <button type="button" class="social apple" (click)="apple()" [disabled]="busy">
          <svg class="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="currentColor" d="M16.365 1.43c0 1.14-.42 2.2-1.13 3.02-.78.9-2.06 1.6-3.18 1.51-.14-1.13.42-2.31 1.07-3.06.74-.84 2.04-1.47 3.24-1.47zM20.5 17.2c-.55 1.27-.82 1.84-1.53 2.97-.99 1.57-2.39 3.53-4.12 3.54-1.54.01-1.94-.99-4.03-.98-2.09.01-2.53.99-4.07.98-1.73-.02-3.05-1.78-4.04-3.35C-.36 16.96-.66 11.31 1.4 8.32c1.04-1.52 2.68-2.42 4.22-2.42 1.57 0 2.56 1 3.86 1 1.26 0 2.03-1 3.85-1 1.37 0 2.82.74 3.86 2.02-3.39 1.86-2.84 6.7.41 8.28z"/>
          </svg>
          <span>{{ 'auth.login.continueWithApple' | translate }}</span>
        </button>
      }

      @if (platform.isNative) {
        <button type="button" class="social google" (click)="google()" [disabled]="busy">
          <svg class="social-icon" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.9-2.26c-.8.54-1.83.86-3.05.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.34z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"/>
          </svg>
          <span>{{ 'auth.login.continueWithGoogle' | translate }}</span>
        </button>
      } @else {
        <div #googleBtn class="gis-btn-container"></div>
      }
      <button type="button" class="social facebook" (click)="facebook()" [disabled]="busy">
        <svg class="social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#1877F2" d="M24 12c0-6.63-5.37-12-12-12S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85V15.47H7.08V12h3.05V9.36c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.96h-1.52c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38C19.61 22.95 24 17.99 24 12z"/>
        </svg>
        <span>{{ 'auth.login.continueWithFacebook' | translate }}</span>
      </button>
      </div>

      <div class="signup-link">
        {{ 'auth.login.newHere' | translate }} <a [routerLink]="signUpUrl" [queryParams]="signUpQuery" (click)="stashAuth()">{{ 'auth.login.createAccount' | translate }}</a>
      </div>
    </div>
  }
</div>
